import React, { Fragment, useCallback, useEffect, useReducer, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import Grid from "@mui/material/Grid";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import ai, { redirectToLogin } from "../../helpers/axios";
import usePolling from "../../helpers/usePolling";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import reducer from "./reducer";

const initialState = {
  loading: true,
  data: [],
  totalLength: 0,
  countPerPage: 10,
  currentPage: 1,
  sortBy: "createdAt",
  sortDirection: "desc",
};

const AcceptedDeclinedLeadsContent = () => {
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getData = useCallback(async (loadSilently) => {
    let loadingStatus = true;
    if (loadSilently) {
      loadingStatus = false;
    }

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    const token = await getAccessTokenSilently().catch(() => {
      redirectToLogin();
    });

    if (!token) return;

    let { data } = await ai.auth(token).get("/api/dashboard/pre-assignment-leads", {
      params: {
        page: state.currentPage,
        countPerPage: state.countPerPage,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      },
    });

    dispatch({
      type: "dataDownloaded",
      payload: {
        data,
      },
    });
  }, [getAccessTokenSilently, state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  usePolling(
    () => getData(true),
    60 * 1000,
    [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection],
  );

  const handlePreAssignmentLeadAction = useCallback(async (leadId, action) => {
    const token = await getAccessTokenSilently().catch(() => {
      redirectToLogin();
    });

    if (!token) return;

    const data = await ai.auth(token).post(`/api/dashboard/pre-assignment-leads/${leadId}/${action}`, {}).catch((e) => console.error(e));
    if (data) {
      await getData(true);
      toast.success(action === "accept" ? "Lead accepted" : "Lead declined");
      if (action === "decline") {
        await getAccessTokenSilently({
          ignoreCache: true,
        });
      } else {
        history.push({ pathname: `/leads/${leadId}` });
      }
    } else {
      toast.error(`Lead ${action} has failed`);
    }
  }, [getAccessTokenSilently, getData, history]);

  const handleAcceptLead = useCallback(
    async (leadId) =>
      handlePreAssignmentLeadAction(leadId, "accept"),
    [handlePreAssignmentLeadAction],
  );

  const handleDeclineLead = useCallback(
    async (leadId) =>
      handlePreAssignmentLeadAction(leadId, "decline"),
    [handlePreAssignmentLeadAction],
  );

  const columns = useMemo(() => [
    {
      name: "Lead created at",
      selector: "createdAt",
      cell: (row) => {
        return row.createdAt ? moment(row.createdAt).format("h:mm a") : "-";
      },
      minWidth: "120px",
      maxWidth: "150px",
    },
    {
      name: "Lead expires at",
      selector: "expiresAt",
      cell: (row) => {
        return row.expiresAt ? moment(row.expiresAt).format("h:mm a") : "-";
      },
      minWidth: "120px",
      maxWidth: "150px",
    },
    {
      name: "Accept or Decline",
      cell: (row) => {
        return <div>
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              handleAcceptLead(row._id);
            }}
          >
            Accept
          </a> /&nbsp;
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              handleDeclineLead(row._id);
            }}
          >
            Decline
          </a>
        </div>;
      },
    },
  ], [handleAcceptLead, handleDeclineLead]);

  if (!state.data.length) {
    return null;
  }

  return (
    <Fragment>
      <Grid item sm={12} lg={6}>
        <PageHeaderRowSearch
          h1="Accept/Decline Leads"
        />

        <div className="server-pagination-table-container trips-list-table-container unassigned-leads-table">
          <StickyHeaderDataTable
            columns={columns}
            data={state.data}
            pagination
            paginationServer
            paginationTotalRows={state.totalLength}
            paginationPerPage={state.countPerPage}
            paginationDefaultPage={state.currentPage}
            paginationRowsPerPageOptions={[5, 10]}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            defaultSortFieldId={state.sortBy}
            sortDirection={state.sortDirection}
            progressPending={state.loading}
            progressComponent={<Loader/>}
            persistTableHead
            highlightOnHover
            responsive
          />
        </div>
      </Grid>
      <Grid item lg={6} style={{
        display: {
          sm: "none",
        },
      }}/>
    </Fragment>
  );
};

export default AcceptedDeclinedLeadsContent;
